import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AdminAuditWidgets from "@/components/admin/AdminAuditWidgets";
import {
  ShieldCheck,
  Plus,
  Trash,
  PencilSimple,
  ChartLineUp,
  Receipt,
  Crown,
  Lock,
  Package,
} from "@phosphor-icons/react";

const ROLE_META = {
  admin: { label: "Admin", icon: Crown, color: "bg-amber-50 text-amber-700 border-amber-200" },
  sales: { label: "Sales", icon: ChartLineUp, color: "bg-blue-50 text-blue-700 border-blue-200" },
  finance: { label: "Finance", icon: Receipt, color: "bg-rose-50 text-rose-700 border-rose-200" },
  inventory: { label: "Inventory", icon: Package, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const ASSIGNABLE_ROLES = ["admin", "sales", "finance", "inventory"];

function RoleChip({ role }) {
  const meta = ROLE_META[role];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[11px] font-medium ${meta.color}`}
      data-testid={`chip-${role}`}
    >
      <Icon size={11} weight="bold" />
      {meta.label}
    </span>
  );
}

export default function AdminRoles() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // existing row or null for new
  const [formEmail, setFormEmail] = useState("");
  const [formRoles, setFormRoles] = useState({ admin: false, sales: false, finance: false, inventory: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/roles");
      setRows(data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load users";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setFormEmail("");
    setFormRoles({ admin: false, sales: false, finance: false, inventory: false });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormEmail(row.email);
    setFormRoles({
      admin: row.roles.includes("admin"),
      sales: row.roles.includes("sales"),
      finance: row.roles.includes("finance"),
      inventory: row.roles.includes("inventory"),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const email = formEmail.trim().toLowerCase();
    if (!email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    const roles = Object.entries(formRoles).filter(([, v]) => v).map(([k]) => k);
    if (roles.length === 0) {
      toast.error("Select at least one role");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/roles", { email, roles });
      toast.success(`Saved roles for ${email}`);
      setDialogOpen(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (row.email === (user?.email || "").toLowerCase()) {
      toast.error("You cannot remove your own admin access.");
      return;
    }
    if (!window.confirm(`Remove all access for ${row.email}?\nThey will be signed out on their next request.`)) return;
    try {
      await api.delete(`/admin/roles/${encodeURIComponent(row.email)}`);
      toast.success(`Removed ${row.email}`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to remove";
      toast.error(msg);
    }
  };

  const stats = useMemo(() => {
    const s = { total: rows.length, admin: 0, sales: 0, finance: 0, inventory: 0 };
    rows.forEach((r) => r.roles.forEach((role) => { s[role] = (s[role] || 0) + 1; }));
    return s;
  }, [rows]);

  return (
    <div data-testid="admin-roles-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Workspace Admin
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              User Roles
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage who can access the Sales, Finance and Inventory workspaces.
            </p>
            <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
              <Lock size={10} weight="bold" /> Bootstrap admins are defined in <code className="font-mono text-slate-500">backend/user_roles.txt</code> and auto-restore on restart.
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            data-testid="add-user-btn"
          >
            <Plus size={14} weight="bold" /> Add user
          </button>
        </div>
      </div>

      <div className="p-8 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
          {[
            { label: "Total users", value: stats.total, accent: "bg-slate-50 text-slate-700", icon: ShieldCheck },
            { label: "Admins", value: stats.admin, accent: "bg-amber-50 text-amber-700", icon: Crown },
            { label: "Sales", value: stats.sales, accent: "bg-blue-50 text-blue-700", icon: ChartLineUp },
            { label: "Finance", value: stats.finance, accent: "bg-rose-50 text-rose-700", icon: Receipt },
            { label: "Inventory", value: stats.inventory, accent: "bg-emerald-50 text-emerald-700", icon: Package },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{s.label}</div>
                  <div className="mt-2 font-heading text-2xl font-bold text-slate-900">{s.value}</div>
                </div>
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${s.accent}`}>
                  <s.icon size={18} weight="duotone" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent role changes + Active users */}
        <AdminAuditWidgets />

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="roles-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Roles</th>
                <th className="px-6 py-3">Updated</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-500">No users yet. Click &quot;Add user&quot; to grant access.</td></tr>
              )}
              {!loading && rows.map((r) => {
                const isMe = r.email === (user?.email || "").toLowerCase();
                const isBootstrap = !!r.is_bootstrap;
                return (
                  <tr key={r.email} className="hover:bg-slate-50" data-testid={`role-row-${r.email}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{r.email}</span>
                        {isBootstrap && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-semibold uppercase tracking-wider"
                            title="Defined in backend/user_roles.txt. Auto-restores on restart if deleted. Edit the file to revoke."
                            data-testid={`bootstrap-${r.email}`}
                          >
                            <Lock size={9} weight="bold" /> Bootstrap
                          </span>
                        )}
                      </div>
                      {isMe && (
                        <div className="text-[10px] uppercase tracking-[0.16em] text-amber-600 font-semibold mt-0.5">
                          That&apos;s you
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {r.roles.map((role) => <RoleChip key={role} role={role} />)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs">
                      <div>{new Date(r.updated_at).toLocaleString()}</div>
                      <div className="text-slate-400">by {r.updated_by}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50"
                          data-testid={`edit-${r.email}`}
                        >
                          <PencilSimple size={12} weight="bold" /> Edit
                        </button>
                        <button
                          onClick={() => remove(r)}
                          disabled={isMe || isBootstrap}
                          className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs font-medium hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`remove-${r.email}`}
                          title={
                            isMe
                              ? "You cannot remove your own admin access"
                              : isBootstrap
                              ? "Bootstrap admin — edit user_roles.txt to revoke"
                              : "Remove user"
                          }
                        >
                          <Trash size={12} weight="bold" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit roles for ${editing.email}` : "Add user to workspace"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                disabled={!!editing}
                placeholder="user@svraf.com"
                className="mt-1.5"
                data-testid="form-email"
              />
              {!editing && (
                <p className="text-xs text-slate-500 mt-1.5">
                  Must match the Google account they sign in with.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Roles</Label>
              <div className="mt-2 space-y-2">
                {ASSIGNABLE_ROLES.map((role) => {
                  const meta = ROLE_META[role];
                  const Icon = meta.icon;
                  return (
                    <label
                      key={role}
                      className={
                        "flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition " +
                        (formRoles[role] ? `${meta.color} border-2` : "bg-white border-slate-200 hover:border-slate-300")
                      }
                      data-testid={`role-toggle-${role}`}
                    >
                      <Checkbox
                        checked={formRoles[role]}
                        onCheckedChange={(v) => setFormRoles((p) => ({ ...p, [role]: !!v }))}
                      />
                      <Icon size={16} weight="duotone" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{meta.label}</div>
                        <div className="text-xs text-slate-500">
                          {role === "admin" && "Full access to all workspaces + this admin page"}
                          {role === "sales" && "Sales workspace: dashboard, customers, pipeline, orders, vendors"}
                          {role === "finance" && "Finance workspace: invoices, AR follow-ups, products, settings"}
                          {role === "inventory" && "Inventory workspace: items, stock, recipes, production, procurement & QC"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
              data-testid="save-role-btn"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add user"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
